import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FileText,
  FileImage,
  FileSpreadsheet,
  ExternalLink,
  Download,
  Trash2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRole } from '@/hooks/useRole';
import { getDocumentSignedUrl, useDeleteDocument } from './useDocuments';
import { DOCUMENT_TYPE_COLORS, DOCUMENT_TYPE_LABELS } from './types';
import type { ClinanceDocument } from './types';

function MimeIcon({ mime, size = 18 }: { mime: string; size?: number }) {
  if (mime.startsWith('image/')) return <FileImage size={size} className="text-purple-500" />;
  if (mime.includes('spreadsheet') || mime === 'text/csv' || mime === 'application/vnd.ms-excel')
    return <FileSpreadsheet size={size} className="text-green-600" />;
  return <FileText size={size} className="text-primary" />;
}

function bytesToReadable(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(2)} MB`;
}

interface Props {
  doc: ClinanceDocument;
  readOnly?: boolean;
}

export function DocumentRow({ doc, readOnly = false }: Props) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteDocument();
  const { isStaff } = useRole();
  const canDelete = isStaff && !readOnly;

  const sanitizedName = doc.file_path.split('/').pop()?.replace(/^\d+_/, '') ?? doc.name;

  const openInNewTab = async () => {
    setBusy(true);
    const url = await getDocumentSignedUrl(doc.file_path);
    setBusy(false);
    if (!url) {
      toast.error('Não foi possível gerar o link.');
      return;
    }
    window.open(url, '_blank', 'noreferrer');
  };

  const downloadFile = async () => {
    setBusy(true);
    try {
      const url = await getDocumentSignedUrl(doc.file_path);
      if (!url) throw new Error('Sem URL.');
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = sanitizedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(`Erro ao baixar: ${e instanceof Error ? e.message : 'desconhecido'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-start gap-3 rounded-lg border border-border bg-white p-3 transition-colors hover:bg-secondary/40">
        <div className="mt-0.5 flex-shrink-0">
          <MimeIcon mime={doc.mime_type} size={22} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-foreground">{doc.name}</h4>
            <span
              className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${DOCUMENT_TYPE_COLORS[doc.document_type]}`}
            >
              {DOCUMENT_TYPE_LABELS[doc.document_type]}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="truncate">📎 {sanitizedName}</span>
            <span>{bytesToReadable(doc.size_bytes)}</span>
            {doc.document_date && (
              <span>
                📅{' '}
                {format(new Date(`${doc.document_date}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            )}
            {doc.supplier_name && <span>🏢 {doc.supplier_name}</span>}
            {doc.transaction_id && <span className="text-primary">↗ vinculado a transação</span>}
          </div>
          {doc.notes && (
            <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{doc.notes}</p>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={openInNewTab}
            disabled={busy}
            className="h-8 text-xs"
            title="Abrir em nova aba"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadFile}
            disabled={busy}
            className="h-8 text-xs"
            title="Baixar"
          >
            <Download size={12} />
          </Button>
          {canDelete && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              disabled={remove.isPending}
              className="h-8 text-xs text-expense hover:bg-expense/10 hover:text-expense"
              title="Excluir"
            >
              <Trash2 size={12} />
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir documento?</AlertDialogTitle>
            <AlertDialogDescription>
              "{doc.name}" será removido permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(doc)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
